import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBBtn, MDBInput } from "mdb-react-ui-kit";
import { usePageState } from "../context/AppStateContext";
import axios from "axios";
import "./ViewProductsPage.css";

const marketplaces = [
  { id: "ebay", name: "eBay", color: "primary" },
  { id: "shopify", name: "Shopify", color: "success" },
  { id: "poshmark", name: "Poshmark", color: "secondary" },
];

const ViewProductsPage = () => {
  const navigate = useNavigate();

  const { state, setState, clearState } = usePageState("viewProducts");

  const {
    currentPage = 1,
    itemsPerPage = 5,
    searchQuery = "",
    categoryFilter = "",
    marketplaceFilter = "",
    selectedProductIds = [],
    expandedProduct = null,
  } = state;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const company_id = localStorage.getItem("company_id");

        const { data: productsRes } = await axios.get(
          "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/products",
          {
            params: { company_id }
          }
        );

        const productsData = productsRes.data || [];

        const productsWithMarketplaces = await Promise.all(
          productsData.map(async (product) => {
            let ebayFields = {};
            let shopifyFields = {};

            // eBay
            try {
              const res = await axios.get(
                `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/ebay-product-by-product/${product.id}`,
              );
              const ebayData = res.data?.data;
              if (ebayData) {
                if (ebayData.aspects) {
                  Object.entries(ebayData.aspects).forEach(([key, value]) => {
                    const val = Array.isArray(value) ? value.join(", ") : value;
                    if (val !== "N/A") ebayFields[key] = val;
                  });
                }
                ebayFields.SKU = ebayData.sku || "";
                ebayFields.Price = `${ebayData.currency || "$"}${ebayData.price || 0}`;
                ebayFields.Quantity = ebayData.quantity || 0;
                ebayFields.Condition = ebayData.condition || "";
                ebayFields["eBay Item ID"] = ebayData.ebay_item_id || "";
              }
            } catch {
              console.warn(`No eBay data for product ${product.id}`);
            }

            // Shopify
            try {
              const res = await axios.get(
                `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/shopify-product/${product.id}`,
              );
              const shopifyData = res.data?.data;
              if (shopifyData) {
                shopifyFields["Shopify Product ID"] = shopifyData.shopify_product_id || "";
                shopifyFields["Variant ID"] = shopifyData.shopify_variant_id || "";
                shopifyFields["Inventory Item ID"] = shopifyData.inventory_item_id || "";
                shopifyFields.SKU = shopifyData.sku || "";
                shopifyFields.Price = `$${shopifyData.price || 0}`;
                shopifyFields.Quantity = shopifyData.quantity || 0;
                shopifyFields.Vendor = shopifyData.vendor || "";
                shopifyFields["Product Type"] = shopifyData.product_type || "";
              }
            } catch {
              console.warn(`No Shopify data for product ${product.id}`);
            }

            return {
              ...product,
              uploadedImages: product.images?.length
                ? product.images
                : ["https://via.placeholder.com/120"],
              selectedMarketplaces: [
                "local",
                ...(Object.keys(ebayFields).length ? ["ebay"] : []),
                ...(Object.keys(shopifyFields).length ? ["shopify"] : []),
              ],
              marketplaces: {
                local: {
                  Title: product.title,
                  SKU: product.sku || "",
                  Price: `$${product.price || 0}`,
                  Quantity: product.quantity || 0,
                  Category: product.category || "Other",
                },
                ...(Object.keys(ebayFields).length && { ebay: ebayFields }),
                ...(Object.keys(shopifyFields).length && {
                  shopify: shopifyFields,
                }),
              },
            };
          }),
        );

        setProducts(productsWithMarketplaces);
      } catch (error) {
        console.error("API ERROR:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const categories = [...new Set(products.map((p) => p.category))];

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch =
        searchQuery === "" ||
        product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        categoryFilter === "" || product.category === categoryFilter;

      const matchesMarketplace =
        marketplaceFilter === "" ||
        product.selectedMarketplaces.includes(marketplaceFilter);

      return matchesSearch && matchesCategory && matchesMarketplace;
    });
  }, [products, searchQuery, categoryFilter, marketplaceFilter]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setState({ currentPage: page });
    }
  };

  const handleSelectProduct = (productId) => {
    const isSelected = selectedProductIds.includes(productId);
    const newSelection = isSelected
      ? selectedProductIds.filter((id) => id !== productId)
      : [...selectedProductIds, productId];
    setState({ selectedProductIds: newSelection });
  };

  const handleSelectAll = () => {
    const allIds = paginatedProducts.map((p) => p.id);
    const allSelected = allIds.every((id) => selectedProductIds.includes(id));

    if (allSelected) {
      setState({
        selectedProductIds: selectedProductIds.filter(
          (id) => !allIds.includes(id),
        ),
      });
    } else {
      setState({
        selectedProductIds: [...new Set([...selectedProductIds, ...allIds])],
      });
    }
  };

  const handleToggleExpand = (productId) => {
    setState({
      expandedProduct: expandedProduct === productId ? null : productId,
    });
  };

  const handleClearFilters = () => {
    clearState();
  };

  if (loading) {
    return (
      <MDBContainer className="py-5 text-center">
        <h4>Loading products...</h4>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer className="py-4">
      <h2 className="mb-4">View Products</h2>

      <div className="filters-section p-3 mb-4 bg-light rounded shadow-sm">
        <div className="row g-3 align-items-end">
          <div className="col-md-3">
            <MDBInput
              label="Search products..."
              value={searchQuery}
              onChange={(e) =>
                setState({ searchQuery: e.target.value, currentPage: 1 })
              }
            />
          </div>

          <div className="col-md-2">
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) =>
                setState({ categoryFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <select
              className="form-select"
              value={marketplaceFilter}
              onChange={(e) =>
                setState({ marketplaceFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Marketplaces</option>
              {marketplaces.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <select
              className="form-select"
              value={itemsPerPage}
              onChange={(e) =>
                setState({
                  itemsPerPage: parseInt(e.target.value),
                  currentPage: 1,
                })
              }
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>

          <div className="col-md-3">
            <MDBBtn color="secondary" size="sm" onClick={handleClearFilters}>
              <i className="fas fa-times me-1"></i> Clear Filters
            </MDBBtn>
          </div>
        </div>

        {(searchQuery || categoryFilter || marketplaceFilter) && (
          <div className="mt-2">
            <small className="text-muted">
              Showing {filteredProducts.length} of {products.length} products
              {searchQuery && (
                <span className="badge bg-info ms-2">
                  Search: "{searchQuery}"
                </span>
              )}
              {categoryFilter && (
                <span className="badge bg-info ms-2">
                  Category: {categoryFilter}
                </span>
              )}
              {marketplaceFilter && (
                <span className="badge bg-info ms-2">
                  Marketplace: {marketplaceFilter}
                </span>
              )}
            </small>
          </div>
        )}
      </div>

      {selectedProductIds.length > 0 && (
        <div className="alert alert-primary mb-3">
          <strong>{selectedProductIds.length}</strong> product(s) selected
          <MDBBtn
            color="link"
            size="sm"
            className="ms-2"
            onClick={() => setState({ selectedProductIds: [] })}
          >
            Clear Selection
          </MDBBtn>
        </div>
      )}

      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="selectAll"
            checked={
              paginatedProducts.length > 0 &&
              paginatedProducts.every((p) => selectedProductIds.includes(p.id))
            }
            onChange={handleSelectAll}
          />
          <label className="form-check-label" htmlFor="selectAll">
            Select All on Page
          </label>
        </div>
      </div>

      <div className="product-list">
        {paginatedProducts.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-box-open fa-3x mb-3"></i>
            <p>No products found matching your filters.</p>
          </div>
        ) : (
          paginatedProducts.map((product) => (
            <div
              key={product.id}
              className={`product-row p-3 mb-3 shadow-sm rounded ${
                selectedProductIds.includes(product.id)
                  ? "border-primary border-2"
                  : ""
              }`}
              style={{ backgroundColor: "#fff" }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div className="form-check me-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedProductIds.includes(product.id)}
                      onChange={() => handleSelectProduct(product.id)}
                    />
                  </div>
                  <img
                    src={product.uploadedImages[0]}
                    alt={product.title}
                    className="img-thumbnail me-3"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                    }}
                  />
                  <div>
                    <h5 className="mb-1">{product.title}</h5>
                    <p className="mb-1 text-muted">{product.description}</p>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <span className="badge bg-secondary">
                        Price: ${product.price}
                      </span>
                      <span className="badge bg-secondary">
                        Category: {product.category}
                      </span>
                      <span className="badge bg-secondary">
                        Condition: {product.condition}
                      </span>
                      <span className="badge bg-secondary">
                        Quantity: {product.quantity}
                      </span>
                    </div>
                    <div className="mb-2">
                      {product.selectedMarketplaces.map((id) => {
                        const market = marketplaces.find((m) => m.id === id);
                        return (
                          <span key={id} className="market-badge">
                            {market?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column gap-2 ms-3">
                  <MDBBtn
                    size="sm"
                    color="info"
                    outline
                    onClick={() => handleToggleExpand(product.id)}
                  >
                    {expandedProduct === product.id ? "Collapse" : "Expand"}
                  </MDBBtn>
                  
                  <MDBBtn
                    size="sm"
                    color="danger"
                    onClick={() =>
                      navigate(`/products/${product.id}`, {
                        state: { product: product },
                      })
                    }
                  >
                    View Details
                  </MDBBtn>

                  <MDBBtn
                    size="sm"
                    color="warning"
                    onClick={() => navigate(`/products/update/${product.id}`)}
                  >
                    Update
                  </MDBBtn>
                  
                  <MDBBtn size="sm" color="danger">
                    Delete
                  </MDBBtn>
                </div>
              </div>

              {expandedProduct === product.id && (
                <div className="mt-3 border-top pt-3">
                  {product.selectedMarketplaces.map((id) => {
                    const market = marketplaces.find((m) => m.id === id);
                    const fields = product.marketplaces[id] || {};
                    return (
                      <div
                        key={id}
                        className={`marketplace-card p-3 mb-2 rounded border border-${market?.color}`}
                      >
                        <h6 className={`text-${market?.color} mb-3`}>
                          {market?.name} Details
                        </h6>
                        <div className="row">
                          {Object.entries(fields)
                            .filter(
                              ([_, v]) =>
                                v !== "" && v !== null && v !== undefined,
                            )
                            .map(([k, v]) => (
                              <div key={k} className="col-md-6 col-lg-4 mb-3">
                                <div className="detail-box p-2 rounded bg-light h-100">
                                  <small className="text-muted d-block mb-1">
                                    {k}
                                  </small>
                                  <strong className="text-dark">{v}</strong>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <li
                key={page}
                className={`page-item ${currentPage === page ? "active" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </li>
          </ul>
          <p className="text-center text-muted">
            Page {currentPage} of {totalPages} ({filteredProducts.length} items)
          </p>
        </nav>
      )}
    </MDBContainer>
  );
};

export default ViewProductsPage;